<template>
  <HrPageLayout
    title="Pension & Gratuity"
    subtitle="Monthly remittances, beneficiaries, and accumulated pension"
    icon="🏛"
    :breadcrumb-items="[{ label: 'Home', route: '/dashboard' }, { label: 'Pension & Benefits' }, { label: 'Pension & Gratuity' }]"
  >
    <div class="director-module">
      <div class="tabs-row">
        <button v-for="t in tabs" :key="t.id" type="button" class="tab-btn" :class="{ active: activeTab === t.id }" @click="activeTab = t.id; load()">
          {{ t.label }}
        </button>
      </div>

      <!-- Remittances -->
      <div v-show="activeTab === 'remittances'" class="card-section">
        <div class="filters-row">
          <select v-model="filters.year" @change="loadRemittances">
            <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
          </select>
          <select v-model="filters.month" @change="loadRemittances">
            <option value="">All months</option>
            <option v-for="m in 12" :key="m" :value="m">{{ monthName(m) }}</option>
          </select>
          <select v-model="filters.employeeId" @change="loadRemittances" class="emp-select">
            <option value="">All employees</option>
            <option v-for="emp in employees" :key="emp.id" :value="emp.id">{{ emp.firstName }} {{ emp.lastName }} ({{ emp.departmentName }})</option>
          </select>
          <button type="button" class="hr-btn hr-btn-primary" @click="openRemittanceModal">Add remittance</button>
        </div>
        <div class="table-wrap">
          <table class="table table-bordered table-sm">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Period</th>
                <th>Pension (KES)</th>
                <th>Gratuity (KES)</th>
                <th>Remittance date</th>
                <th>Reference</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in remittances" :key="r.id">
                <td>{{ employeeName(r.employee_id) }}</td>
                <td>{{ r.period_year }}-{{ String(r.period_month).padStart(2,'0') }}</td>
                <td>{{ formatNum(r.pension_amount) }}</td>
                <td>{{ formatNum(r.gratuity_amount) }}</td>
                <td>{{ r.remittance_date || '–' }}</td>
                <td>{{ r.reference_number || '–' }}</td>
              </tr>
              <tr v-if="!remittances.length"><td colspan="6" class="text-muted">No remittances</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Beneficiaries (by employee) -->
      <div v-show="activeTab === 'beneficiaries'" class="card-section">
        <div class="filters-row">
          <label>Employee</label>
          <select v-model="selectedEmployeeId" @change="loadBeneficiaries" class="emp-select">
            <option value="">Select employee</option>
            <option v-for="emp in employees" :key="emp.id" :value="emp.id">{{ emp.firstName }} {{ emp.lastName }}</option>
          </select>
          <button type="button" class="hr-btn hr-btn-primary" :disabled="!selectedEmployeeId" @click="openBeneficiaryModal">Update beneficiaries</button>
        </div>
        <div v-if="accumulated.total_pension !== undefined" class="accumulated-box">
          <strong>Accumulated:</strong> Pension KES {{ formatNum(accumulated.total_pension) }}, Gratuity KES {{ formatNum(accumulated.total_gratuity) }}
        </div>
        <div class="table-wrap">
          <table class="table table-bordered table-sm">
            <thead>
              <tr><th>Name</th><th>Relationship</th><th>Share %</th><th>Primary</th></tr>
            </thead>
            <tbody>
              <tr v-for="b in beneficiaries" :key="b.id">
                <td>{{ b.beneficiary_name }}</td>
                <td>{{ b.relationship }}</td>
                <td>{{ b.percentage_share }}</td>
                <td>{{ b.is_primary ? 'Yes' : '' }}</td>
              </tr>
              <tr v-if="!beneficiaries.length && selectedEmployeeId"><td colspan="4" class="text-muted">No beneficiaries</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Accumulated -->
      <div v-show="activeTab === 'accumulated'" class="card-section">
        <div class="filters-row">
          <select v-model="selectedEmployeeId" @change="loadAccumulated" class="emp-select">
            <option value="">Select employee</option>
            <option v-for="emp in employees" :key="emp.id" :value="emp.id">{{ emp.firstName }} {{ emp.lastName }} ({{ emp.departmentName }})</option>
          </select>
        </div>
        <div v-if="selectedEmployeeId && accumulated" class="accumulated-box large">
          <div>Total Pension: <strong>KES {{ formatNum(accumulated.total_pension) }}</strong></div>
          <div>Total Gratuity: <strong>KES {{ formatNum(accumulated.total_gratuity) }}</strong></div>
        </div>
      </div>

      <!-- Modals (simplified - could be separate components) -->
      <div v-if="showRemittanceModal" class="modal-overlay" @click.self="showRemittanceModal = false">
        <div class="modal-card">
          <h3>Add remittance</h3>
          <div class="form-group">
            <label>Employee</label>
            <select v-model="remittanceForm.employee_id" required>
              <option value="">Select</option>
              <option v-for="emp in employees" :key="emp.id" :value="emp.id">{{ emp.firstName }} {{ emp.lastName }}</option>
            </select>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Year</label>
              <input v-model.number="remittanceForm.period_year" type="number" min="2020" max="2030">
            </div>
            <div class="form-group">
              <label>Month</label>
              <select v-model.number="remittanceForm.period_month">
                <option v-for="m in 12" :key="m" :value="m">{{ monthName(m) }}</option>
              </select>
            </div>
          </div>
          <div class="form-group">
            <label>Pension amount (KES)</label>
            <input v-model.number="remittanceForm.pension_amount" type="number" step="0.01">
          </div>
          <div class="form-group">
            <label>Gratuity amount (KES)</label>
            <input v-model.number="remittanceForm.gratuity_amount" type="number" step="0.01">
          </div>
          <div class="form-group">
            <label>Remittance date</label>
            <input v-model="remittanceForm.remittance_date" type="date">
          </div>
          <div class="form-group">
            <label>Reference number</label>
            <input v-model="remittanceForm.reference_number" type="text">
          </div>
          <div class="modal-actions">
            <button type="button" class="hr-btn hr-btn-secondary" @click="showRemittanceModal = false">Cancel</button>
            <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="saveRemittance">Save</button>
          </div>
        </div>
      </div>

      <div v-if="showBeneficiaryModal" class="modal-overlay" @click.self="showBeneficiaryModal = false">
        <div class="modal-card">
          <h3>Update beneficiaries ({{ selectedEmployeeName }})</h3>
          <p class="text-muted small">Add one or more beneficiaries. Sum of shares should be 100%.</p>
          <div v-for="(b, i) in beneficiaryForm" :key="i" class="beneficiary-row">
            <input v-model="b.beneficiary_name" placeholder="Full name">
            <input v-model="b.relationship" placeholder="Relationship">
            <input v-model.number="b.percentage_share" type="number" step="0.01" placeholder="%">
            <label><input v-model="b.is_primary" type="checkbox"> Primary</label>
            <button type="button" class="hr-btn hr-btn-sm hr-btn-danger" @click="beneficiaryForm.splice(i,1)">Remove</button>
          </div>
          <button type="button" class="hr-btn hr-btn-secondary btn-sm" @click="beneficiaryForm.push({ beneficiary_name: '', relationship: '', percentage_share: 0, is_primary: false })">Add beneficiary</button>
          <div class="modal-actions mt-2">
            <button type="button" class="hr-btn hr-btn-secondary" @click="showBeneficiaryModal = false">Cancel</button>
            <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="saveBeneficiaries">Save</button>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl } from "@/api";

export default {
  name: "PensionGratuity",
  components: { HrPageLayout },
  data() {
    return {
      activeTab: "remittances",
      tabs: [
        { id: "remittances", label: "Monthly remittances" },
        { id: "beneficiaries", label: "Beneficiaries" },
        { id: "accumulated", label: "Accumulated pension" }
      ],
      filters: { year: new Date().getFullYear(), month: "", employeeId: "" },
      yearOptions: [],
      employees: [],
      remittances: [],
      beneficiaries: [],
      accumulated: {},
      selectedEmployeeId: "",
      selectedEmployeeName: "",
      showRemittanceModal: false,
      showBeneficiaryModal: false,
      saving: false,
      remittanceForm: {
        employee_id: "",
        period_year: new Date().getFullYear(),
        period_month: new Date().getMonth() + 1,
        pension_amount: 0,
        gratuity_amount: 0,
        remittance_date: "",
        reference_number: ""
      },
      beneficiaryForm: []
    };
  },
  created() {
    const y = new Date().getFullYear();
    for (let i = y; i >= y - 5; i--) this.yearOptions.push(i);
    this.loadEmployees();
    this.load();
  },
  methods: {
    load() {
      if (this.activeTab === "remittances") this.loadRemittances();
      else if (this.activeTab === "beneficiaries") this.loadBeneficiaries();
      else if (this.activeTab === "accumulated") this.loadAccumulated();
    },
    loadEmployees() {
      const fd = new FormData();
      fd.append("function", "getEmployees");
      fd.append("page", "1");
      fd.append("limit", "500");
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success && res.data.data && res.data.data.employees)
          this.employees = res.data.data.employees;
      }).catch(() => {});
    },
    loadRemittances() {
      const fd = new FormData();
      fd.append("function", "getPensionRemittances");
      fd.append("year", this.filters.year);
      if (this.filters.month) fd.append("month", this.filters.month);
      if (this.filters.employeeId) fd.append("employeeId", this.filters.employeeId);
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success && res.data.data && res.data.data.remittances)
          this.remittances = res.data.data.remittances;
        else this.remittances = [];
      }).catch(() => { this.remittances = []; });
    },
    loadBeneficiaries() {
      if (!this.selectedEmployeeId) {
        this.beneficiaries = [];
        this.accumulated = {};
        return;
      }
      const fd = new FormData();
      fd.append("function", "getPensionBeneficiaries");
      fd.append("employeeId", this.selectedEmployeeId);
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success && res.data.data && res.data.data.beneficiaries)
          this.beneficiaries = res.data.data.beneficiaries;
        else this.beneficiaries = [];
      }).catch(() => { this.beneficiaries = []; });
      const fd2 = new FormData();
      fd2.append("function", "getPensionAccumulated");
      fd2.append("employeeId", this.selectedEmployeeId);
      execute(fd2, authUrl).then((res) => {
        if (res.data && res.data.success && res.data.data)
          this.accumulated = res.data.data;
      }).catch(() => {});
    },
    loadAccumulated() {
      if (!this.selectedEmployeeId) return;
      const fd = new FormData();
      fd.append("function", "getPensionAccumulated");
      fd.append("employeeId", this.selectedEmployeeId);
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success && res.data.data)
          this.accumulated = res.data.data;
      }).catch(() => {});
    },
    employeeName(id) {
      if (!id) return '–';
      const e = this.employees.find((x) => x.id == id);
      return e ? `${e.firstName || ''} ${e.lastName || ''}`.trim() || id : id;
    },
    monthName(m) {
      const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      return names[m - 1] || m;
    },
    formatNum(n) {
      if (n == null) return "0";
      return Number(n).toLocaleString("en-KE", { minimumFractionDigits: 2 });
    },
    openRemittanceModal() {
      this.remittanceForm = {
        employee_id: "",
        period_year: this.filters.year,
        period_month: this.filters.month || new Date().getMonth() + 1,
        pension_amount: 0,
        gratuity_amount: 0,
        remittance_date: "",
        reference_number: ""
      };
      this.showRemittanceModal = true;
    },
    saveRemittance() {
      if (!this.remittanceForm.employee_id) return;
      this.saving = true;
      const fd = new FormData();
      fd.append("function", "savePensionRemittance");
      Object.keys(this.remittanceForm).forEach((k) => fd.append(k, this.remittanceForm[k] == null ? "" : this.remittanceForm[k]));
      execute(fd, authUrl).then((res) => {
        this.saving = false;
        if (res.data && res.data.success) {
          this.showRemittanceModal = false;
          this.loadRemittances();
        } else {
          alert(res.data && res.data.message ? res.data.message : "Save failed");
        }
      }).catch(() => { this.saving = false; });
    },
    openBeneficiaryModal() {
      const emp = this.employees.find((e) => e.id == this.selectedEmployeeId);
      this.selectedEmployeeName = emp ? `${emp.firstName} ${emp.lastName}` : "";
      this.beneficiaryForm = this.beneficiaries.length
        ? this.beneficiaries.map((b) => ({ ...b, is_primary: !!b.is_primary }))
        : [{ beneficiary_name: "", relationship: "", percentage_share: 0, is_primary: false }];
      this.showBeneficiaryModal = true;
    },
    saveBeneficiaries() {
      if (!this.selectedEmployeeId) return;
      this.saving = true;
      const fd = new FormData();
      fd.append("function", "savePensionBeneficiaries");
      fd.append("employee_id", this.selectedEmployeeId);
      fd.append("beneficiaries", JSON.stringify(this.beneficiaryForm));
      execute(fd, authUrl).then((res) => {
        this.saving = false;
        if (res.data && res.data.success) {
          this.showBeneficiaryModal = false;
          this.loadBeneficiaries();
        } else {
          alert(res.data && res.data.message ? res.data.message : "Save failed");
        }
      }).catch(() => { this.saving = false; });
    }
  }
};
</script>

<style scoped>
.director-module { padding: 0 0 24px; }
.tabs-row { display: flex; gap: 8px; margin-bottom: 16px; }
.tab-btn { padding: 8px 16px; border: 1px solid #dee2e6; border-radius: 6px; background: #fff; cursor: pointer; }
.tab-btn.active { background: #0d6efd; color: #fff; border-color: #0d6efd; }
.card-section { background: #fff; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); padding: 16px; margin-bottom: 16px; }
.filters-row { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin-bottom: 16px; }
.filters-row label { font-weight: 600; }
.emp-select { min-width: 220px; }
.table-wrap { overflow-x: auto; }
.table { width: 100%; font-size: 13px; }
.accumulated-box { padding: 12px; background: #f8f9fa; border-radius: 6px; margin-bottom: 12px; }
.accumulated-box.large { font-size: 1.1rem; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-card { background: #fff; border-radius: 8px; padding: 24px; max-width: 480px; width: 90%; max-height: 90vh; overflow-y: auto; }
.form-group { margin-bottom: 12px; }
.form-group label { display: block; font-weight: 600; margin-bottom: 4px; }
.form-row { display: flex; gap: 12px; }
.beneficiary-row { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-bottom: 8px; }
.beneficiary-row input[placeholder="Full name"] { min-width: 140px; }
.modal-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; }
.mt-2 { margin-top: 16px; }
.small { font-size: 12px; }
.text-muted { color: #6c757d; }
</style>
